 import React, { useEffect, useMemo, useState } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToShortGuestReservations,
  upsertShortstayReservation,
} from "../../../../redux/shortstayReservationSlice";
import { listenToMultiShortstays } from "../../../../redux/shortstaysSlice";



import { Gift } from "phosphor-react";

  import dayjs from "dayjs";




export default function ShortAccomodationBookings() {
  const [open, setOpen] = useState(false);
  const { userId } = useParams<{ userId: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
  
    // ✅ selectors
    const reservations = useSelector(
      (state: RootState) => state.shortstayReservations.guestReservations
    );
    const listings = useSelector((state: RootState) => state.shortstays.listings);
    const currentUser = useSelector((state: RootState) => state.auth.user); // <-- from authSlice
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);
  const authLoaded = useSelector(
  (state: RootState) => state.auth.authLoaded
);

    const [tab, setTab] = useState(0);
    const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  
const activeReservations = useMemo(() => {
  return reservations
    .filter((r) => !r.isCancelled && !r.isCompleted)
    .sort((a, b) => {
      const dateA = a.checkIn ? new Date(a.checkIn).getTime() : 0;
      const dateB = b.checkIn ? new Date(b.checkIn).getTime() : 0;
      return dateA - dateB; // nearest date first
    });
}, [reservations]);


// ✅ Load reservations for guest
useEffect(() => {
  if (authLoaded && userId) {
    dispatch(listenToShortGuestReservations(userId));
  }
}, [dispatch, authLoaded, userId]);



useEffect(() => {
  if (!authLoaded) return;

  if (!currentUser) {
    navigate("/AuthSwitch", { replace: true });
    return;
  }

  if (userId && currentUser.userId !== userId) {
    navigate("/", { replace: true });
  }
}, [authLoaded, currentUser, userId, navigate]);

  
    // ✅ Fetch missing listings
    useEffect(() => {
      const fetchedListingIds = listings.map((l) => l.listingId);
      const missingIds = reservations
        .map((r) => r.listingId)
        .filter((id) => !fetchedListingIds.includes(id));
  
      if (missingIds.length > 0) {
        const chunk = (arr: string[], size = 10) =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
          );
        chunk(missingIds).forEach((chunkedIds) => {
          dispatch(listenToMultiShortstays(chunkedIds));
        });
      }
    }, [reservations, listings, dispatch]);




  
    const listingsMap = useMemo(() => {
      const map: { [listingId: string]: any } = {};
      listings.forEach((l) => {
        map[l.listingId] = l;
      });
      return map;
    }, [listings]);


  
    const findHostProfile = (hostId: string) => {
    if (!hostId) return null;
    return allUsers.find((u) => u.userId === hostId) || null;
  };
  
  
    const getListingPhoto = (listingId: string): string => {
      const listing = listingsMap[listingId];
      const photo = listing?.photos?.[0];
      return photo?.length > 5 ? photo : "/default-shortstay.jpg";
    };
  
    const formatAddress = (addr: any) => {
      if (!addr) return "";
      return [
        addr.street,
        addr.apt ? `Apt ${addr.apt}` : "",
        addr.city,
        addr.county,
        addr.zip,
        addr.country,
      ]
        .filter(Boolean)
        .join(", ");
    };
  
    // ✅ Handle Check-In toggle
    const handleCheckInToggle = async (res: any) => {
      try {
        if (res.hasCheckedIn) return;
        await dispatch(
          upsertShortstayReservation({
            ...res,
            hasCheckedIn: true,
            paymentId: res.paymentId,
          })
        );
      } catch (err) {
        console.error("Failed to update check-in:", err);
      }
    };
  
    const openGoogleMaps = (lat: string, lng: string) => {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, "_blank");
    };
  



const renderStatuses = (res: any) => {
  if (!res) return null;

  const hostFlags: Record<string, string> = {
    isArrivingSoon: "Arriving Soon",
    isUpcoming: "Upcoming",
    isOngoing: "Ongoing",
    isCheckingIn: "Checking In",
    isCheckingOut: "Checking Out",
  };

  const active = Object.entries(hostFlags).find(
    ([key]) => res[key] === true
  );

  if (!active) return null;

  const [, label] = active;

  const isCheckingIn = res.isCheckingIn === true;

  return (
    <div
      style={{
        marginBottom: 12,
        padding: "10px 14px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 800,
        textAlign: "center",
        background: isCheckingIn ? "#e7f6ec" : "#fff4e5",
        color: isCheckingIn ? "#1e7f43" : "#b45309",
        border: `1px solid ${
          isCheckingIn ? "#b7e4c7" : "#f5d0a6"
        }`,
      }}
    >
      {label}
    </div>
  );
};



return (
    <div style={styles.centerWrap}>
{activeReservations.map((reservation) => {

  const listing = listingsMap[reservation.listingId];

  const prettyCheckIn = reservation.checkIn
    ? dayjs(reservation.checkIn).format("DD MMM YYYY")
    : "—";

  const prettyCheckOut = reservation.checkOut
    ? dayjs(reservation.checkOut).format("DD MMM YYYY")
    : "—";

  const adults = reservation.adults ?? reservation.guestCount ?? 1;
  const children = reservation.children ?? 0;
  const infants = reservation.infants ?? 0;
  const pets = reservation.pets ?? 0;

  const isPerHour =
    reservation.rateType === "perHour" ||
    reservation.rateType === "perHourPerPerson";

  const totalHours = reservation.totalHours ?? 0;

  const needsGuestVerification =
    reservation.guestCount > 1 &&
    reservation.hasGuestList !== true;

  const durationValue = isPerHour
    ? reservation.totalHours || 0
    : reservation.reservedDuration || 0;
    const unitLabel = isPerHour ? "hour" : "night";


  const unitRate = listing?.rate || 0;

  const extrasTotal = listing?.extras
    ? Object.values(listing.extras).reduce(
        (a: number, b: any) => a + Number(b),
        0
      )
    : 0;

  const serviceFee = reservation.guestsFee || 0;
  const totalPaid = reservation.totalPaid || 0;


  const today = dayjs().startOf("day");
const checkInDate = reservation.checkIn
  ? dayjs(reservation.checkIn).startOf("day")
  : null;

  const now = dayjs();
const checkInDateTime = reservation.checkIn
  ? dayjs(reservation.checkIn)
  : null;

const isWithin24Hours = !!(
  checkInDateTime &&
  checkInDateTime.diff(now, "hour") <= 24 &&
  checkInDateTime.isAfter(now)
);

  const disableAccess =
  needsGuestVerification || !isWithin24Hours;

const isCheckInTodayOrPast = !!(
  checkInDate &&
  (checkInDate.isSame(today) || checkInDate.isBefore(today))
);


  return (
    <aside key={reservation.id} style={styles.stickyCard}>

{/* 🏠 LISTING SUMMARY */}
<section style={styles.listingCard}>
  <div style={styles.listingLeft}>
    <strong style={styles.listingTitle}>
      {listing?.title || "—"}
    </strong>

    {/* 📍 CLICKABLE ADDRESS */}
    <div
      style={{
        ...styles.listingAddress,
        textDecoration: "underline",
        cursor: listing?.address ? "pointer" : "default",
      }}
      onClick={() => {
        if (!listing?.address?.latitude) return;
        openGoogleMaps(
          listing.address.latitude,
          listing.address.longitude
        );
      }}
    >
      {formatAddress(listing?.address) || "View on map"}
    </div>
  </div>

  {/* 🖼 IMAGE */}
  <div style={styles.listingImageWrap}>
    {listing?.photos?.[0] ? (
      <img
        src={listing.photos[0]}
        alt="Listing"
        style={styles.listingImage}
      />
    ) : (
      <div style={styles.imagePlaceholder} />
    )}
  </div>
</section>


        {/* 📅 INPUT CARD */}
        {/* 📅 INPUT CARD */}
<div style={styles.inputCard}>
  <div style={styles.grid2}>
    {/* CHECK-IN */}
    <div style={styles.inputCell}>
      <div style={styles.inputLabel}>CHECK-IN</div>
      <div style={styles.inputValue}>
        {prettyCheckIn}
      </div>
    </div>

    {/* CHECK-OUT / DATE */}
    <div style={{ ...styles.inputCell, borderLeft: "1px solid #ddd" }}>
      <div style={styles.inputLabel}>
        {isPerHour ? "DATE" : "CHECKOUT"}
      </div>
      <div style={styles.inputValue}>
        {isPerHour ? "Same day" : prettyCheckOut}
      </div>
    </div>
  </div>

  {/* GUESTS / HOURS */}
  <div style={styles.guestRow}>
    <div style={styles.inputLabel}>
      {isPerHour ? "HOURS" : "GUESTS"}
    </div>

    <div style={styles.inputValue}>
      {isPerHour ? (
        <>
          {totalHours} hour{totalHours !== 1 ? "s" : ""}
        </>
      ) : (
        <>
          {adults} adult{adults !== 1 ? "s" : ""}
          {children ? `, ${children} child${children !== 1 ? "ren" : ""}` : ""}
          {infants ? `, ${infants} infant${infants !== 1 ? "s" : ""}` : ""}
          {pets ? `, ${pets} pet${pets !== 1 ? "s" : ""}` : ""}
        </>
      )}
    </div>
  </div>
</div>


        <div style={{ flex: 1 }} />

        {/* 🔴 GUEsts BUTTON (DUMB) */}
{reservation &&
  reservation.guestCount > 1 &&
  !reservation.hasGuestList && (
    <button
      style={styles.guestsBtn}
      onClick={() =>
        navigate(`/shortguestlist/${userId}`, {
          state: {
            reservationId: reservation.id,
            listingId: reservation.listingId,
            guestCount: reservation.guestCount,
          },
        })
      }
    >
      ➕ ADD OTHER GUEST(S) DETAILS
    </button>
)}



{/* 🚀 RESERVATION ACTIONS CARD */}
{reservation && (
  <div style={styles.actionsCard}>

    {/* TOP ROW */}
    <div style={styles.row2}>
<button
  style={{
    ...styles.actionBtn,
    ...(reservation.hasCheckedIn || isCheckInTodayOrPast
      ? styles.disabledBtn
      : {}),
  }}
  disabled={reservation.hasCheckedIn || isCheckInTodayOrPast}
onClick={() =>
  navigate(
    `/cancelshortaccomodationbooking/${userId}/${reservation.listingId}/${reservation.id}`,
    { state: { res: reservation } }
  )
}

>
  Change Reservation
</button>


      <button
        style={{
          ...styles.actionBtn,
          ...(!reservation.isCheckingOut &&
            !reservation.isCompleted &&
            styles.disabledBtn),
        }}
        disabled={
          !reservation.isCheckingOut &&
          !reservation.isCompleted
        }
        onClick={() =>
          navigate(
            `/shortrating/${userId}/${reservation.listingId}/${reservation.id}`
          )
        }
      >
        Review Stay
      </button>
    </div>

    {/* MIDDLE FULL WIDTH */}
<button
  style={{
    ...styles.actionBtnFull,
    ...(disableAccess && styles.disabledBtn),
  }}
  disabled={disableAccess}
  onClick={() =>
    !disableAccess &&
    openGoogleMaps(
      listing?.address?.latitude,
      listing?.address?.longitude
    )
  }
>
  Directions
</button>

<button
  style={{
    ...styles.actionBtnFull,
    ...(disableAccess && styles.disabledBtn),
  }}
  disabled={disableAccess}
  onClick={() =>
    !disableAccess &&
    navigate(`/shortchecking/${reservation.listingId}`)
  }
>
  Check-In Details
</button>

    {/* BOTTOM ROW */}
    <div style={styles.row2}>
      <button
        style={{
          ...styles.actionBtn,
          ...(needsGuestVerification && styles.disabledBtn),
        }}
        disabled={needsGuestVerification}
        onClick={() =>
          !needsGuestVerification &&
          navigate(`/shortreceipt/${userId}/${reservation.id}`)
        }
      >
        Get Receipt
      </button>

      <button
        style={{
          ...styles.actionBtn,
          ...(reservation.isCheckingIn && !needsGuestVerification
            ? styles.liveCheckIn
            : styles.disabledBtn),
        }}
        disabled={
          needsGuestVerification ||
          !reservation.isCheckingIn ||
          reservation.hasCheckedIn
        }
        onClick={() =>
          !needsGuestVerification &&
          reservation.isCheckingIn &&
          handleCheckInToggle(reservation)
        }
      >
        Check-In
      </button>
    </div>

  </div>
)}


        {/* ℹ️ NOTE */}
      <section style={styles.card}>
  {renderStatuses(reservation)}

  <div style={styles.mutedText}>
    Current Status
  </div>
</section>


   {/* 📕 PAYMENT DETAILS */}
{reservation && listing && (
  <div style={styles.bookCard}>
    {/* HEADER */}
    <div
      style={styles.bookHeader}
      onClick={() => setOpen((o) => !o)}
    >
      <strong>Payment details</strong>
      {open ? <ExpandLess /> : <ExpandMore />}
    </div>

    {/* BODY */}
    <motion.div
      initial={false}
      animate={{
        height: open ? "auto" : 0,
        opacity: open ? 1 : 0,
      }}
      transition={{ duration: 0.45, ease: "easeInOut" }}
      style={styles.whitePage}
    >
      <div style={styles.pageTop} />

      <div style={styles.pageContent}>
        {/* ===== STAY ===== */}
        <div style={styles.sectionTitle}>Your stay</div>

        <div style={styles.tableRow}>
          <span>
            {durationValue} {unitLabel}
            {durationValue !== 1 ? "s" : ""} × €{unitRate}

          </span>
          <span>
            €{(unitRate * durationValue).toFixed(2)}
          </span>
        </div>

        {/* ===== EXTRAS ===== */}
        {extrasTotal > 0 && (
          <>
            <div style={styles.sectionTitle}>Extras</div>
            <div style={styles.tableRow}>
              <span>Extras total</span>
              <span>€{extrasTotal.toFixed(2)}</span>
            </div>
          </>
        )}

        {/* ===== FEES ===== */}
        <div style={styles.sectionTitle}>Fees</div>

        <div style={styles.tableRow}>
          <span>Service fee</span>
          <span>€{serviceFee.toFixed(2)}</span>
        </div>

        {/* ===== TOTAL ===== */}
        <div
          style={{
            ...styles.tableRow,
            fontWeight: 900,
            fontSize: 15,
          }}
        >
          <span>Total paid</span>
          <span>€{totalPaid.toFixed(2)}</span>
        </div>
      </div>
    </motion.div>
  </div>
)}

      </aside>
    );
  })}

    </div>
  );

}



/* ───────────── STYLES ───────────── */

const styles: Record<string, React.CSSProperties> = {
centerWrap: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 24,
  padding: "20px 16px",
  width: "100%",
  maxWidth: 1100,
  margin: "0 auto",
  boxSizing: "border-box",
},


stickyCard: {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #eee",
  borderRadius: 18,
  padding: 24,
  boxShadow: "0 10px 30px rgba(0,0,0,.12)",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
},


  card: {
    padding: 16,
    borderRadius: 16,
    border: "1px solid #eee",
    marginBottom: 12,
  },

  mutedText: {
    fontSize: 13,
    color: "#666",
  },

  inputCard: {
    border: "1px solid #ddd",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: 16,
    background: "#fff",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },

  guestRow: {
    borderTop: "1px solid #ddd",
    padding: "10px 12px",
  },

  inputCell: {
    padding: "12px 14px",
  },

  inputLabel: {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.6,
    color: "#222",
  },

  inputValue: {
    fontSize: 14,
    fontWeight: 600,
    marginTop: 2,
    color: "#717171",
  },

  guestsBtn: {
    width: "100%",
    padding: 14,
    background: "#cc0000",
    border: "none",
    fontWeight: 900,
    cursor: "pointer",
    color: "#fff",
    fontSize: 16,
    marginBottom: 16,
  },

  listingCard: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,

  padding: 16,
  borderRadius: 16,
  border: "1px solid #eee",
  marginBottom: 12,
  background: "#fff",
},

listingLeft: {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  flex: 1,
},

listingTitle: {
  fontSize: 15,
  fontWeight: 900,
  lineHeight: 1.25,
  color: "#222",
},

listingAddress: {
  fontSize: 13,
  color: "#717171",
  lineHeight: 1.3,
},

listingImageWrap: {
  width: 64,
  height: 64,
  borderRadius: 12,
  overflow: "hidden",
  cursor: "pointer",
  flexShrink: 0,
  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
},

listingImage: {
  width: "100%",
  height: "100%",
  objectFit: "cover",
},

  /* 📕 BOOK */
bookCard: {
  width: "87%",
  border: "2px solid #cc0000",
  borderRadius: 20,
  padding: 24,
  boxShadow:
    "0 10px 25px rgba(0,0,0,.12), inset 0 0 6px rgba(0,0,0,.04)",
},


  bookHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  bookTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#ff48a5",
  },
  coverSection: {
    position: "relative",
    marginTop: 16,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 6px 18px rgba(255,72,165,0.4)",
  },
  image: { width: "100%", height: 200, objectFit: "cover" },
  coverOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(transparent, rgba(0,0,0,0.9))",
    padding: "12px 16px",
  },
  coverTitle: { fontSize: 14, color: "#ddd" },
  coverAmount: {
    fontSize: 28,
    fontWeight: 800,
    color: "#ffd700",
    textShadow: "0 0 8px rgba(255,215,0,.4)",
  },
  whitePage: {
    background: "#fff",
    borderRadius: 14,
    marginTop: 16,
    boxShadow: "inset 0 0 8px rgba(0,0,0,.05),0 3px 12px rgba(0,0,0,.25)",
  },
  pageTop: {
    height: 4,
    background: "repeating-linear-gradient(90deg,#ddd 0 10px,#fff 10px 20px)",
  },
  pageContent: {
    padding: "16px 20px",
    background: "repeating-linear-gradient(#fff, #fff 28px, #f7f7f7 29px)",
  },
  tableRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
    color: "#222",
    padding: "8px 0",
    borderBottom: "1px dashed rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 10,
    fontWeight: 700,
    color: "#ad1212ff",
  },
  downloadBtn: {
    width: "100%",
    marginTop: 20,
    background: "linear-gradient(145deg,#b700ff,#ff48a5,#ffd700)",
    border: "none",
    borderRadius: 40,
    color: "#fff",
    fontWeight: 700,
    fontSize: 16,
    padding: "12px 0",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
  },
  loading: {
    background: "#000",
    color: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },

  //actions
  actionsCard: {
  border: "1px solid #ddd",
  borderRadius: 12,
  overflow: "hidden",
  background: "#fff",
  marginBottom: 16,
},

row2: {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
},

actionBtn: {
  background: "#fff",
  color: "#cc0000",
  fontWeight: 800,
  fontSize: 13,
  padding: "16px 10px",
  cursor: "pointer",
  border: "1.5px solid #cc0000",
  borderBottom: "1px solid #ddd",
  transition: "all 0.2s ease",
},

actionBtnFull: {
  width: "100%",
  background: "#fff",
  color: "#cc0000",
  fontWeight: 800,
  fontSize: 13,
  padding: "16px 10px",
  cursor: "pointer",
  border: "1.5px solid #cc0000",
  borderBottom: "1px solid #ddd",
  transition: "all 0.2s ease",
},

disabledBtn: {
  background: "#f3f3f3",
  color: "#aaa",
  borderColor: "#ddd",
  cursor: "not-allowed",
},

liveCheckIn: {
  background: "#cc0000",
  color: "#fff",
  borderColor: "#b30000",
  boxShadow: "0 0 12px rgba(204,0,0,0.6)",
},

};